import { randomUuid } from './chunk-WVLHT3DL.js';
import { utf8Encode, sha256Bytes, bytesToHex } from './chunk-QXYP3KHC.js';
import { brandClass } from './chunk-N7SZ66QO.js';

// src/signing/index.ts
var ALG = {
  name: "ECDSA",
  namedCurve: "P-256",
  hash: { name: "SHA-256" }
};
function subtle() {
  const c = globalThis.crypto;
  if (!c?.subtle) throw new Error("Web Crypto (crypto.subtle) is unavailable in this runtime.");
  return c.subtle;
}
function canonicalJson(value) {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const obj = value;
  const entries = Object.keys(obj).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(obj[k])}`);
  return `{${entries.join(",")}}`;
}
function toBytes(input) {
  if (input instanceof Uint8Array) return input;
  const text = typeof input === "string" ? input : canonicalJson(input);
  return new TextEncoder().encode(text);
}
function b64url(bytes) {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  for (const b of arr) bin += String.fromCharCode(b);
  const b64 = typeof btoa !== "undefined" ? btoa(bin) : Buffer.from(arr).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function generateSigningKeyPair(extractable = false) {
  return subtle().generateKey(ALG, extractable, ["sign", "verify"]);
}
async function exportPublicJwk(key) {
  return subtle().exportKey("jwk", key);
}
async function importPublicJwk(jwk) {
  return subtle().importKey("jwk", jwk, ALG, true, ["verify"]);
}
async function sign(privateKey, payload) {
  const sig = await subtle().sign(ALG, privateKey, toBytes(payload));
  return b64url(sig);
}
async function verify(publicKey, payload, signatureB64Url) {
  const sig = Uint8Array.from(
    atobUrl(signatureB64Url),
    (c) => c.charCodeAt(0)
  );
  return subtle().verify(ALG, publicKey, sig, toBytes(payload));
}
function atobUrl(s) {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
  return typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
}
var TwoKeySigner = class _TwoKeySigner {
  constructor(root, intermediate, rootKeyId, certificate = null) {
    this.root = root;
    this.intermediate = intermediate;
    this.rootKeyId = rootKeyId;
    this.certificate = certificate;
  }
  root;
  intermediate;
  rootKeyId;
  certificate;
  static {
    brandClass(this, "signing/TwoKeySigner");
  }
  static async create(opts = { rootKeyId: "root" }) {
    const root = await generateSigningKeyPair(false);
    const intermediate = await generateSigningKeyPair(false);
    const signer = new _TwoKeySigner(root, intermediate, opts.rootKeyId ?? "root");
    await signer.rotateIntermediate(opts.intermediateTtlMs ?? 15 * 6e4);
    return signer;
  }
  /**
   * Build a signer from an ALREADY-PERSISTED root keypair — e.g. a
   * non-extractable device root `CryptoKeyPair` loaded from IndexedDB — minting a
   * fresh short-lived intermediate. This is the enrolment-aware path: the root
   * key is generated + persisted (and enrolled server-side) ONCE, then reused
   * across sessions so the enrolled `rootKeyId` keeps resolving server-side while
   * the private key never leaves the device.
   */
  static async fromRootKeyPair(opts) {
    const intermediate = await generateSigningKeyPair(false);
    const signer = new _TwoKeySigner(opts.rootKeyPair, intermediate, opts.rootKeyId);
    await signer.rotateIntermediate(opts.intermediateTtlMs ?? 15 * 6e4);
    return signer;
  }
  /** Re-certify the intermediate key with a fresh expiry (root signs it). */
  async rotateIntermediate(ttlMs = 15 * 6e4) {
    const intermediatePublicJwk = await exportPublicJwk(this.intermediate.publicKey);
    const expiresAt = Date.now() + ttlMs;
    const body = { intermediatePublicJwk, rootKeyId: this.rootKeyId, expiresAt };
    const signature = await sign(this.root.privateKey, body);
    this.certificate = { ...body, signature };
    return this.certificate;
  }
  getCertificate() {
    if (!this.certificate) throw new Error("Intermediate key not yet certified \u2014 call rotateIntermediate().");
    return this.certificate;
  }
  /** Sign a request payload with the intermediate key. */
  async signRequest(payload) {
    if (!this.certificate || Date.now() > this.certificate.expiresAt) await this.rotateIntermediate();
    return { signature: await sign(this.intermediate.privateKey, payload), certificate: this.getCertificate() };
  }
  /** Export the root public key (registered with the backend on enrolment). */
  exportRootPublicJwk() {
    return exportPublicJwk(this.root.publicKey);
  }
};
async function verifyTwoKeyRequest(rootPublicKey, certificate, payload, requestSignature) {
  if (Date.now() > certificate.expiresAt) return false;
  const certBody = {
    intermediatePublicJwk: certificate.intermediatePublicJwk,
    rootKeyId: certificate.rootKeyId,
    expiresAt: certificate.expiresAt
  };
  if (!await verify(rootPublicKey, certBody, certificate.signature)) return false;
  const intermediate = await importPublicJwk(certificate.intermediatePublicJwk);
  return verify(intermediate, payload, requestSignature);
}
var HMAC_BLOCK = 64;
function concatBytes(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
function hmacSha256Hex(key, message) {
  let keyBytes = typeof key === "string" ? utf8Encode(key) : key;
  const msgBytes = typeof message === "string" ? utf8Encode(message) : message;
  if (keyBytes.length > HMAC_BLOCK) keyBytes = sha256Bytes(keyBytes);
  const block = new Uint8Array(HMAC_BLOCK);
  block.set(keyBytes);
  const ipad = new Uint8Array(HMAC_BLOCK);
  const opad = new Uint8Array(HMAC_BLOCK);
  for (let i = 0; i < HMAC_BLOCK; i++) {
    ipad[i] = block[i] ^ 54;
    opad[i] = block[i] ^ 92;
  }
  const inner = sha256Bytes(concatBytes(ipad, msgBytes));
  return bytesToHex(sha256Bytes(concatBytes(opad, inner)));
}
function assembleKey(key) {
  const parts = [...key.base];
  if (key.splice) {
    parts.splice(key.splice.at, 0, ...key.splice.from.slice(key.splice.start, key.splice.end));
  }
  if (key.prepend) {
    return key.prepend.from.slice(key.prepend.start, key.prepend.end).concat(parts).join("");
  }
  return parts.join("");
}
var KEYRING_DECOYS = [
  // index 0 — used when (timestamp % 2 === 0)
  {
    base: ["Z", "n", "V", "j", "a", "y", "U", "g", "Y", "n", "J", "v", "d", "G", "h", "l", "c", "g", "=", "="],
    splice: { at: 6, from: ["9", "L", "9", "P", "c", "n", "f", "5", "V", "d", "B", "5", "b", "3", "j", "c", "9", "U", "w", "6"], start: 10, end: 14 }
  },
  // index 1 — used when (timestamp % 2 === 1)
  {
    base: ["d", "G", "h", "l", "c", "i", "B", "m", "d", "W", "N", "r", "I", "H", "l", "v", "d", "Q", "=", "="],
    prepend: { from: ["X", "O", "r", "i", "i", "6", "H", "H", "Y", "n", "J", "v", "p", "4", "f", "E", "O", "x", "v", "U"], start: 8, end: 12 }
  }
];
var BUILD_INJECTED_KEYRING = typeof __AUTHE_REQUEST_KEY_KEYRING__ !== "undefined" && Array.isArray(__AUTHE_REQUEST_KEY_KEYRING__) ? __AUTHE_REQUEST_KEY_KEYRING__ : KEYRING_DECOYS;
var REQUEST_KEY_VERSION = typeof __AUTHE_REQUEST_KEY_VERSION__ !== "undefined" && __AUTHE_REQUEST_KEY_VERSION__ ? __AUTHE_REQUEST_KEY_VERSION__ : "v1";
var REQUEST_KEY_HEADER = "REQUEST-KEY";
var REQUEST_TS_HEADER = "REQUEST-TS";
var REQUEST_NONCE_HEADER = "REQUEST-NONCE";
var REQUEST_POW_HEADER = "REQUEST-POW";
function canonicalQuery(params) {
  if (!params) return "";
  const keys = Object.keys(params).sort();
  return keys.map((k) => `${k}=${String(params[k])}`).join("&");
}
function defaultMessage(input) {
  const q = canonicalQuery(input.params);
  const base = `${input.url}${q ? (input.url.includes("?") ? "&" : "?") + q : ""}${input.timestamp}`;
  return input.nonce ? `${base}${input.nonce}` : base;
}
function generateRequestKey(input, opts = {}) {
  const keyring = opts.keyring ?? BUILD_INJECTED_KEYRING;
  if (keyring.length === 0) throw new Error("generateRequestKey: empty keyring");
  const selected = keyring[Math.abs(input.timestamp) % keyring.length];
  const key = assembleKey(selected);
  const message = (opts.messageBuilder ?? defaultMessage)(input);
  const r = hmacSha256Hex(key, message);
  const digit = parseInt(r[0], 16) % 10;
  const combined = r.slice(0, 12) + String(digit) + r.slice(-12);
  const chars = combined.split("");
  for (let i = 0; i < chars.length; i++) {
    if (parseInt(r[i % 64], 16) % 10 > 6) chars[i] = chars[i].toUpperCase();
  }
  return chars.join("");
}
function requestKeyHeaders(input, opts) {
  const version = input.version ?? REQUEST_KEY_VERSION;
  const timestamp = input.timestamp ?? Date.now();
  const nonce = input.nonce ?? randomUuid();
  const message = publicSignedMessage(version, timestamp, nonce);
  return {
    [REQUEST_KEY_HEADER]: generateRequestKey(
      { url: "", timestamp, nonce },
      { keyring: opts?.keyring, messageBuilder: opts?.messageBuilder ?? (() => message) }
    ),
    [REQUEST_TS_HEADER]: String(timestamp),
    [REQUEST_NONCE_HEADER]: nonce,
    "X-Request-Key-Version": version
  };
}
function graphqlRequestKeyHeaders(opts = {}) {
  const version = opts.version ?? REQUEST_KEY_VERSION;
  const timestamp = opts.timestamp ?? Date.now();
  const nonce = opts.nonce ?? randomUuid();
  const message = publicSignedMessage(version, timestamp, nonce);
  return {
    [REQUEST_KEY_HEADER]: generateRequestKey(
      { url: "", timestamp, nonce },
      { keyring: opts.keyring, messageBuilder: () => message }
    ),
    [REQUEST_TS_HEADER]: String(timestamp),
    [REQUEST_NONCE_HEADER]: nonce,
    "X-Request-Key-Version": version
  };
}
function publicSignedMessage(version, timestamp, nonce) {
  return `${version}:${timestamp}:${nonce}`;
}
function leadingZeroBits(digest) {
  let bits = 0;
  for (const byte of digest) {
    if (byte === 0) {
      bits += 8;
      continue;
    }
    bits += 8 - (32 - Math.clz32(byte));
    break;
  }
  return bits;
}
var POW_MAX_ITERATIONS = 1 << 26;
function solvePow(challenge, bits, maxIterations = POW_MAX_ITERATIONS) {
  if (bits <= 0) return "0";
  for (let i = 0; i < maxIterations; i++) {
    const digest = sha256Bytes(utf8Encode(`${challenge}:${i}`));
    if (leadingZeroBits(digest) >= bits) return String(i);
  }
  throw new Error(`proof-of-work solution not found within ${maxIterations} iterations`);
}
function publicApiAuthToken(opts = {}) {
  const version = opts.version ?? REQUEST_KEY_VERSION;
  const timestamp = opts.timestamp ?? Date.now();
  const nonce = opts.nonce ?? randomUuid();
  const message = publicSignedMessage(version, timestamp, nonce);
  const requestKey = generateRequestKey(
    { url: "", timestamp },
    { keyring: opts.keyring, messageBuilder: () => message }
  );
  const token = { v: version, t: timestamp, k: requestKey, n: nonce };
  if (opts.powBits && opts.powBits > 0) token.pow = solvePow(message, opts.powBits);
  return b64url(utf8Encode(JSON.stringify(token)));
}
function publicApiRequestKeyHeaders(opts = {}) {
  const version = opts.version ?? REQUEST_KEY_VERSION;
  const timestamp = opts.timestamp ?? Date.now();
  const nonce = opts.nonce ?? randomUuid();
  const message = publicSignedMessage(version, timestamp, nonce);
  const headers = {
    [REQUEST_KEY_HEADER]: generateRequestKey(
      { url: "", timestamp, nonce },
      { keyring: opts.keyring, messageBuilder: () => message }
    ),
    [REQUEST_TS_HEADER]: String(timestamp),
    [REQUEST_NONCE_HEADER]: nonce,
    "X-Request-Key-Version": version
  };
  if (opts.powBits && opts.powBits > 0) headers[REQUEST_POW_HEADER] = solvePow(message, opts.powBits);
  return headers;
}
function publicTwoKeyPayload(nonce, timestamp) {
  return { nonce, timestamp };
}
async function publicApiTwoKeyToken(signer, opts = {}) {
  const timestamp = opts.timestamp ?? Date.now();
  const nonce = opts.nonce ?? randomUuid();
  const { signature, certificate } = await signer.signRequest(publicTwoKeyPayload(nonce, timestamp));
  return b64url(
    utf8Encode(JSON.stringify({ scheme: "two_key", t: timestamp, n: nonce, sig: signature, cert: certificate }))
  );
}
var AUTH_SIGNATURE_HEADER = "X-Auth-Signature";
var AUTH_CERTIFICATE_HEADER = "X-Auth-Certificate";
var AUTH_NONCE_HEADER = "X-Auth-Nonce";
var AUTH_TIMESTAMP_HEADER = "X-Auth-Timestamp";
async function twoKeyRequestHeaders(signer, opts = {}) {
  const timestamp = opts.timestamp ?? Date.now();
  const nonce = opts.nonce ?? randomUuid();
  const { signature, certificate } = await signer.signRequest(publicTwoKeyPayload(nonce, timestamp));
  return {
    [AUTH_SIGNATURE_HEADER]: signature,
    [AUTH_CERTIFICATE_HEADER]: b64url(utf8Encode(JSON.stringify(certificate))),
    [AUTH_NONCE_HEADER]: String(nonce),
    [AUTH_TIMESTAMP_HEADER]: String(timestamp)
  };
}

export { AUTH_CERTIFICATE_HEADER, AUTH_NONCE_HEADER, AUTH_SIGNATURE_HEADER, AUTH_TIMESTAMP_HEADER, REQUEST_KEY_HEADER, REQUEST_KEY_VERSION, REQUEST_NONCE_HEADER, REQUEST_POW_HEADER, REQUEST_TS_HEADER, TwoKeySigner, exportPublicJwk, generateRequestKey, generateSigningKeyPair, graphqlRequestKeyHeaders, hmacSha256Hex, importPublicJwk, publicApiAuthToken, publicApiRequestKeyHeaders, publicApiTwoKeyToken, publicSignedMessage, publicTwoKeyPayload, requestKeyHeaders, sign, solvePow, twoKeyRequestHeaders, verify, verifyTwoKeyRequest };
//# sourceMappingURL=chunk-4SSFR73N.js.map
//# sourceMappingURL=chunk-4SSFR73N.js.map